import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'
import { toast } from 'sonner'
import { QrCode, Copy, Check, Clock, UserCheck, Building2, RefreshCw, Ticket, Ban } from 'lucide-react'
import api from '@/services/api'
import { fieldService, EnrollmentMode, EnrollmentResponse, EnrollmentTokenSummary } from '@/services/fieldService'

interface UserOption {
    id: number
    fullName: string
    username: string
}

interface AgencyOption {
    id: number
    name: string
    code: string
}

export default function EnrollmentPage() {
    const { t } = useTranslation()
    const [mode, setMode] = useState<EnrollmentMode>('USER_BOUND')

    const [users, setUsers] = useState<UserOption[]>([])
    const [agencies, setAgencies] = useState<AgencyOption[]>([])

    const [boundUserId, setBoundUserId] = useState<number>(0)
    const [agencyId, setAgencyId] = useState<number>(0)
    const [maxRedemptions, setMaxRedemptions] = useState<number>(50)
    const [ttlMinutes, setTtlMinutes] = useState<number>(720)

    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState<EnrollmentResponse | null>(null)
    const [copied, setCopied] = useState(false)

    const [tokens, setTokens] = useState<EnrollmentTokenSummary[]>([])
    const [tokensLoading, setTokensLoading] = useState(false)
    const [tokensError, setTokensError] = useState(false)
    const [revokingId, setRevokingId] = useState<number | null>(null)

    const loadTokens = async () => {
        setTokensLoading(true)
        setTokensError(false)
        try {
            const data = await fieldService.listEnrollments()
            setTokens(data)
        } catch (error) {
            console.error('Error loading enrollment tokens', error)
            setTokensError(true)
        } finally {
            setTokensLoading(false)
        }
    }

    useEffect(() => {
        loadTokens()
    }, [])

    const handleRevoke = async (id: number) => {
        setRevokingId(id)
        try {
            await fieldService.revokeEnrollment(id)
            toast.success(t('enrollment.toastRevoked'))
            await loadTokens()
        } catch (error) {
            console.error('Error revoking enrollment token', error)
            toast.error(t('enrollment.toastRevokeError'))
        } finally {
            setRevokingId(null)
        }
    }

    const tokenStatus = (token: EnrollmentTokenSummary): { label: string; cls: string } => {
        if (!token.active) return { label: t('enrollment.statusInactive'), cls: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
        if (new Date(token.expiresAt).getTime() < Date.now()) return { label: t('enrollment.statusExpired'), cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
        return { label: t('enrollment.statusActive'), cls: 'bg-green-500/20 text-green-400 border-green-500/30' }
    }

    const isExpired = (t: EnrollmentTokenSummary) => new Date(t.expiresAt).getTime() < Date.now()

    useEffect(() => {
        const loadOptions = async () => {
            try {
                const [usersRes, agenciesRes] = await Promise.all([
                    api.get('/api/v1/users').catch(() => ({ data: [] })),
                    api.get('/api/v1/institutions').catch(() => ({ data: [] })),
                ])
                const usersData = (usersRes.data || []) as any[]
                setUsers(usersData.map((u) => ({
                    id: u.id,
                    fullName: u.fullName || u.username,
                    username: u.username,
                })))
                const agenciesData = (agenciesRes.data || []) as any[]
                setAgencies(agenciesData.map((a) => ({
                    id: a.id,
                    name: a.name,
                    code: a.code,
                })))
            } catch {
                toast.error(t('enrollment.toastLoadOptionsError'))
            }
        }
        loadOptions()
    }, [])

    const handleGenerate = async () => {
        if (mode === 'USER_BOUND' && !boundUserId) {
            toast.error(t('enrollment.toastSelectUser'))
            return
        }
        if (mode === 'PROVISION' && !agencyId) {
            toast.error(t('enrollment.toastSelectInstitution'))
            return
        }

        setLoading(true)
        setResult(null)
        try {
            const payload =
                mode === 'USER_BOUND'
                    ? { mode, boundUserId }
                    : { mode, agencyId, maxRedemptions, ttlMinutes }
            const data = await fieldService.generateEnrollment(payload)
            setResult(data)
            toast.success(t('enrollment.toastGenerated'))
        } catch (error) {
            console.error('Error generating enrollment', error)
            toast.error(t('enrollment.toastGenerateError'))
        } finally {
            setLoading(false)
        }
    }

    const handleCopy = async () => {
        if (!result) return
        try {
            await navigator.clipboard.writeText(result.enrollmentToken)
            setCopied(true)
            toast.success(t('enrollment.toastCopied'))
            setTimeout(() => setCopied(false), 2000)
        } catch {
            toast.error(t('enrollment.toastCopyError'))
        }
    }

    return (
        <div className="space-y-6 max-w-5xl">
            {/* Header */}
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-600/20">
                    <QrCode className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('enrollment.title')}</h1>
                    <p className="text-gray-400 mt-1">{t('enrollment.subtitle')}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Configuration Panel */}
                <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6 space-y-6">
                    {/* Mode Selector */}
                    <div>
                        <label className="block text-sm text-gray-400 mb-2 font-medium">{t('enrollment.enrollmentMode')}</label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setMode('USER_BOUND')}
                                className={`p-4 rounded-xl border transition-all text-left ${mode === 'USER_BOUND'
                                    ? 'bg-purple-600/10 border-purple-500/40 text-purple-300'
                                    : 'bg-gray-900/40 border-gray-700 text-gray-400 hover:border-gray-600'
                                    }`}
                            >
                                <UserCheck className="w-5 h-5 mb-2" />
                                <div className="text-sm font-bold">{t('enrollment.modeUser')}</div>
                                <div className="text-[10px] opacity-70 mt-0.5">{t('enrollment.modeUserDesc')}</div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('PROVISION')}
                                className={`p-4 rounded-xl border transition-all text-left ${mode === 'PROVISION'
                                    ? 'bg-purple-600/10 border-purple-500/40 text-purple-300'
                                    : 'bg-gray-900/40 border-gray-700 text-gray-400 hover:border-gray-600'
                                    }`}
                            >
                                <Building2 className="w-5 h-5 mb-2" />
                                <div className="text-sm font-bold">{t('enrollment.modeProvision')}</div>
                                <div className="text-[10px] opacity-70 mt-0.5">{t('enrollment.modeProvisionDesc')}</div>
                            </button>
                        </div>
                    </div>

                    {/* USER_BOUND fields */}
                    {mode === 'USER_BOUND' && (
                        <div>
                            <label htmlFor="bound-user" className="block text-sm text-gray-400 mb-1">{t('enrollment.userToBind')}</label>
                            <select
                                id="bound-user"
                                value={boundUserId}
                                onChange={(e) => setBoundUserId(Number(e.target.value))}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                            >
                                <option value={0}>{t('enrollment.selectUser')}</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>{u.fullName} ({u.username})</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* PROVISION fields */}
                    {mode === 'PROVISION' && (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="agency-select" className="block text-sm text-gray-400 mb-1">{t('enrollment.institution')}</label>
                                <select
                                    id="agency-select"
                                    value={agencyId}
                                    onChange={(e) => setAgencyId(Number(e.target.value))}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                >
                                    <option value={0}>{t('enrollment.selectInstitution')}</option>
                                    {agencies.map((a) => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="max-redemptions" className="block text-sm text-gray-400 mb-1">{t('enrollment.maxRedemptions')}</label>
                                    <input
                                        id="max-redemptions"
                                        type="number"
                                        min={1}
                                        value={maxRedemptions}
                                        onChange={(e) => setMaxRedemptions(Number(e.target.value))}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="ttl-minutes" className="block text-sm text-gray-400 mb-1">{t('enrollment.validityMinutes')}</label>
                                    <input
                                        id="ttl-minutes"
                                        type="number"
                                        min={1}
                                        value={ttlMinutes}
                                        onChange={(e) => setTtlMinutes(Number(e.target.value))}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleGenerate}
                        disabled={loading}
                        className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors font-bold"
                    >
                        {loading ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                {t('enrollment.generating')}
                            </>
                        ) : (
                            <>
                                <QrCode className="w-4 h-4" />
                                {t('enrollment.generateQr')}
                            </>
                        )}
                    </button>
                </div>

                {/* Result Panel */}
                <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6 flex flex-col items-center justify-center min-h-[400px]">
                    {!result ? (
                        <div className="text-center text-gray-600 flex flex-col items-center gap-4">
                            <QrCode className="w-16 h-16 opacity-30" />
                            <p className="text-sm max-w-xs">
                                {t('enrollment.placeholderHint')}
                            </p>
                        </div>
                    ) : (
                        <div className="w-full flex flex-col items-center gap-5 animate-in fade-in zoom-in-95 duration-300">
                            <div className="bg-white p-4 rounded-2xl">
                                <QRCode value={result.qrPayload} size={200} />
                            </div>

                            <div className="w-full">
                                <label className="block text-[10px] text-gray-500 uppercase tracking-widest font-black mb-1">{t('enrollment.enrollmentToken')}</label>
                                <div className="flex gap-2">
                                    <code className="flex-1 bg-gray-900/60 border border-gray-700 rounded-lg px-3 py-2 text-xs text-purple-300 font-mono truncate">
                                        {result.enrollmentToken}
                                    </code>
                                    <button
                                        onClick={handleCopy}
                                        className="px-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors flex items-center"
                                        title={t('enrollment.copyToken')}
                                        aria-label={t('enrollment.copyTokenAria')}
                                    >
                                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="w-full flex items-center gap-2 text-xs text-gray-400 bg-gray-900/40 border border-gray-700/50 rounded-lg px-3 py-2">
                                <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                                <span>{t('enrollment.expires', { date: new Date(result.expiresAt).toLocaleString('es-CL') })}</span>
                            </div>

                            <p className="text-[11px] text-gray-500 text-center leading-relaxed">
                                {t('enrollment.scanHintPrefix')}{' '}
                                <span className="text-purple-400 font-bold">/descargar</span>.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Gestión de Tokens de Enrolamiento */}
            <section className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6 space-y-4" aria-labelledby="tokens-heading">
                <div className="flex items-center justify-between gap-4">
                    <h2 id="tokens-heading" className="text-lg font-semibold text-white flex items-center gap-2">
                        <Ticket className="w-5 h-5 text-purple-400" />
                        {t('enrollment.tokensTitle')}
                    </h2>
                    <button
                        type="button"
                        onClick={loadTokens}
                        disabled={tokensLoading}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg text-gray-300 text-sm transition-colors flex items-center gap-2"
                        aria-label={t('enrollment.refreshAria')}
                    >
                        <RefreshCw className={`w-4 h-4 ${tokensLoading ? 'animate-spin' : ''}`} />
                        {t('enrollment.refresh')}
                    </button>
                </div>

                {tokensLoading ? (
                    <div className="flex items-center justify-center py-10 text-gray-400 gap-3">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span className="text-sm">{t('enrollment.loadingTokens')}</span>
                    </div>
                ) : tokensError ? (
                    <div className="py-10 text-center text-red-400 text-sm">
                        {t('enrollment.tokensError')}
                    </div>
                ) : tokens.length === 0 ? (
                    <div className="py-10 text-center text-gray-500 text-sm">
                        {t('enrollment.noTokens')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-gray-700/50">
                                    <th scope="col" className="py-2 pr-4 font-black">{t('enrollment.colMode')}</th>
                                    <th scope="col" className="py-2 pr-4 font-black">{t('enrollment.colRole')}</th>
                                    <th scope="col" className="py-2 pr-4 font-black">{t('enrollment.colRedemptions')}</th>
                                    <th scope="col" className="py-2 pr-4 font-black">{t('enrollment.colExpires')}</th>
                                    <th scope="col" className="py-2 pr-4 font-black">{t('enrollment.colStatus')}</th>
                                    <th scope="col" className="py-2 font-black text-right">{t('enrollment.colActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tokens.map((token) => {
                                    const status = tokenStatus(token)
                                    return (
                                        <tr key={token.id} className="border-b border-gray-700/30 last:border-0">
                                            <td className="py-3 pr-4 text-gray-300">{token.mode}</td>
                                            <td className="py-3 pr-4 text-gray-300">{token.grantedRole}</td>
                                            <td className="py-3 pr-4 text-gray-300 font-mono">
                                                {token.redemptionCount}/{token.maxRedemptions}
                                            </td>
                                            <td className="py-3 pr-4 text-gray-400 whitespace-nowrap">
                                                {new Date(token.expiresAt).toLocaleString('es-CL')}
                                            </td>
                                            <td className="py-3 pr-4">
                                                <span className={`text-[10px] px-2 py-1 rounded-md font-bold border ${status.cls}`}>
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="py-3 text-right">
                                                {token.active && !isExpired(token) ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRevoke(token.id)}
                                                        disabled={revokingId === token.id}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 text-red-400 border border-red-500/30 rounded-lg text-xs font-bold transition-colors"
                                                        aria-label={t('enrollment.revokeAria', { id: token.id })}
                                                    >
                                                        {revokingId === token.id ? (
                                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                        ) : (
                                                            <Ban className="w-3.5 h-3.5" />
                                                        )}
                                                        {t('enrollment.revoke')}
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-600 text-xs">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    )
}
